import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import SEO from "@/components/shared/SEO";

export default function AdminLogin() {
  const { user, login, loading } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  // If the user becomes authenticated (after login or via existing token), redirect.
  useEffect(() => {
    if (!loading && user) navigate("/admin", { replace: true });
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center" data-testid="admin-login-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#0EA5A4]" />
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const u = await login(email.trim(), password);
      // Navigate immediately on success so the user isn't stuck waiting for the
      // useEffect tick. The redirect is idempotent.
      if (u) navigate("/admin", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail?.message
        || err?.response?.data?.detail
        || (err?.response?.status === 429 ? "Too many attempts — try again in a minute." : "Invalid credentials");
      toast.error(typeof msg === "string" ? msg : "Could not sign in");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#FAFAF9] px-4" data-testid="admin-login-page">
      <SEO title="Admin sign in — Ever Clean Home" description="Sign in to the Ever Clean Home admin panel." noindex />
      <Card className="w-full max-w-md rounded-2xl p-8 border border-[#E5E7EB] shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <span className="w-14 h-14 rounded-xl grid place-items-center overflow-hidden">
            <img src="/brand/logo-everclean-icon-192.png" alt="Ever Clean Home" className="w-12 h-12 object-contain" />
          </span>
          <div>
            <div className="font-display font-extrabold text-lg">Ever Clean Home</div>
            <div className="text-xs text-[#4B5563]">{t("admin.login_subtitle")}</div>
          </div>
        </div>
        <h1 className="font-display text-2xl font-bold mb-6">{t("admin.login_title")}</h1>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form" noValidate>
          <div>
            <Label className="text-sm" htmlFor="login-email">{t("admin.email")}</Label>
            <Input id="login-email" type="email" autoComplete="username" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="rounded-xl mt-1" data-testid="admin-login-email" />
          </div>
          <div>
            <Label className="text-sm" htmlFor="login-password">{t("admin.password")}</Label>
            <Input id="login-password" type="password" autoComplete="current-password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="rounded-xl mt-1" data-testid="admin-login-password" />
          </div>
          <Button type="submit" disabled={submitting || !email || !password}
            className="w-full h-11 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-semibold disabled:opacity-60"
            data-testid="admin-login-submit">
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t("common.loading")}
              </span>
            ) : t("admin.sign_in")}
          </Button>
        </form>
      </Card>
    </div>
  );
}
